import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { 
  Loader2, 
  Trash,
  Trash2,
  RotateCcw,
  FolderPlus,
  Info
} from 'lucide-react';

export function RecycleBinTab({ 
  trashLoading,
  deletedItems,
  restoringId,
  permanentDeleteId,
  fetchDeletedItems,
  handleRestoreItem,
  handlePermanentDelete
}) {
  return (
    <div className="space-y-6">
      <Card className="bg-[#2A3240] border-white/10">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-500/10 rounded-md">
                <Trash className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <CardTitle className="text-xl text-white">Deleted Items</CardTitle>
                <CardDescription className="text-gray-400">Items are permanently deleted after 30 days</CardDescription>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchDeletedItems}
              disabled={trashLoading}
              className="border-white/20 text-gray-300 hover:bg-white/5"
            >
              {trashLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {trashLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-gray-500" />
            </div>
          ) : deletedItems.total_count === 0 ? (
            <div className="text-center py-12">
              <Trash className="w-12 h-12 mx-auto mb-4 text-gray-600" />
              <p className="text-gray-400 text-lg mb-2">Recycle Bin is empty</p>
              <p className="text-gray-500 text-sm">Deleted projects and tenders will appear here</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Deleted Projects */}
              {deletedItems.projects?.length > 0 && (
                <div>
                  <h3 className="text-white font-medium mb-3 flex items-center gap-2">
                    <FolderPlus className="w-4 h-4 text-orange-400" />
                    Deleted Projects ({deletedItems.projects.length})
                  </h3>
                  <div className="space-y-2">
                    {deletedItems.projects.map((project) => (
                      <div 
                        key={project.id}
                        className="flex items-center justify-between p-3 bg-[#1E2530] rounded-lg border border-white/5"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-medium truncate">{project.project_ref}</p>
                          <div className="flex items-center gap-3 text-sm text-gray-400">
                            <span>{project.client_name}</span>
                            <span>•</span>
                            <span>Deleted by {project.deleted_by_name}</span>
                            <span>•</span>
                            <span className={project.days_until_permanent_deletion <= 7 ? 'text-red-400' : 'text-gray-400'}>
                              {project.days_until_permanent_deletion} days left
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 ml-4">
                          <Button
                            size="sm"
                            onClick={() => handleRestoreItem('project', project.id)}
                            disabled={restoringId === project.id}
                            className="bg-green-600 hover:bg-green-700 text-white"
                          >
                            {restoringId === project.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <RotateCcw className="w-3 h-3 mr-1" />
                                Restore
                              </>
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              if (window.confirm(`Permanently delete "${project.project_ref}"? This cannot be undone.`)) {
                                handlePermanentDelete('project', project.id);
                              }
                            }}
                            disabled={permanentDeleteId === project.id}
                            className="text-red-400 hover:bg-red-500/10 hover:text-red-300"
                          >
                            {permanentDeleteId === project.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Trash2 className="w-4 h-4" />
                            )}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Deleted Tenders */}
              {deletedItems.tenders?.length > 0 && (
                <div>
                  <h3 className="text-white font-medium mb-3 flex items-center gap-2">
                    <FolderPlus className="w-4 h-4 text-blue-400" />
                    Deleted Tenders ({deletedItems.tenders.length})
                  </h3>
                  <div className="space-y-2">
                    {deletedItems.tenders.map((tender) => (
                      <div 
                        key={tender.id}
                        className="flex items-center justify-between p-3 bg-[#1E2530] rounded-lg border border-white/5"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-medium truncate">{tender.tender_ref}</p>
                          <div className="flex items-center gap-3 text-sm text-gray-400">
                            <span>{tender.client_name}</span>
                            <span>•</span>
                            <span>Deleted by {tender.deleted_by_name}</span>
                            <span>•</span>
                            <span className={tender.days_until_permanent_deletion <= 7 ? 'text-red-400' : 'text-gray-400'}>
                              {tender.days_until_permanent_deletion} days left
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 ml-4">
                          <Button
                            size="sm"
                            onClick={() => handleRestoreItem('tender', tender.id)}
                            disabled={restoringId === tender.id}
                            className="bg-green-600 hover:bg-green-700 text-white"
                          >
                            {restoringId === tender.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <RotateCcw className="w-3 h-3 mr-1" />
                                Restore
                              </>
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              if (window.confirm(`Permanently delete "${tender.tender_ref}"? This cannot be undone.`)) {
                                handlePermanentDelete('tender', tender.id);
                              }
                            }}
                            disabled={permanentDeleteId === tender.id}
                            className="text-red-400 hover:bg-red-500/10 hover:text-red-300"
                          >
                            {permanentDeleteId === tender.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Trash2 className="w-4 h-4" />
                            )}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="bg-[#2A3240] border-white/10">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-gray-400">
              <p className="font-medium text-blue-300 mb-1">How soft delete works:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Deleted items are moved to recycle bin instead of being permanently removed</li>
                <li>Items can be restored within 30 days</li>
                <li>After 30 days, items are automatically and permanently deleted</li>
                <li>OneDrive/SharePoint files are always preserved</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
